// ============================================================
// Workitect Role System Types
// ============================================================

/**
 * The 6 user roles in hierarchical order (highest to lowest).
 * 'moderator' is legacy and should not be used.
 */
export type AppRole = 'architect' | 'owner' | 'admin' | 'manager' | 'user' | 'visitor';

/** Numeric weight for role hierarchy comparisons */
export const ROLE_HIERARCHY: Record<AppRole, number> = {
  architect: 100,
  owner: 80,
  admin: 60,
  manager: 40,
  user: 20,
  visitor: 10,
};

/** Human-readable labels */
export const ROLE_LABELS: Record<AppRole, string> = {
  architect: 'Architect',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  user: 'Team Member',
  visitor: 'Visitor',
};

/** Role descriptions for UI */
export const ROLE_DESCRIPTIONS: Record<AppRole, string> = {
  architect: 'Site-wide super administrator with full access to all instances.',
  // Ownership invariant: exactly one active Owner per instance. Ownership is
  // reassigned only through public.transfer_instance_ownership() — never by
  // invitation and never by ordinary role editing. See ARCHITECTURE.md §3.
  owner: 'Instance owner with subscription controls and full admin capabilities. Exactly one per instance.',
  admin: 'Instance administrator with user/team management.',
  manager: 'Manages specific teams and spaces within an instance.',
  user: 'Completes assigned work within their spaces.',
  visitor: 'External stakeholder with view/approval access only.',
};

/** Chat personality configuration per role */
export interface ChatPersona {
  tone: string;
  defaultFocus: string;
  jargonLevel: 'technical' | 'moderate' | 'simple' | 'nurturing';
  proactiveAgenda: boolean;
  taskReschedulingViaChat: boolean;
  /** Optional additional system prompt instructions for the AI */
  systemPrompt?: string;
  /** Greeting message shown in the copilot welcome screen */
  greeting: string;
  /** Suggested quick-action prompts for this role */
  quickActions: string[];
}

export const CHAT_PERSONAS: Record<AppRole, ChatPersona> = {
  architect: {
    tone: 'direct and approachable',
    defaultFocus: 'system-wide health, instance management, debugging',
    jargonLevel: 'simple',
    proactiveAgenda: false,
    taskReschedulingViaChat: false,
    greeting: 'System overview ready. All instances operational.',
    quickActions: ['Instance health check', 'Show system status'],
  },
  owner: {
    tone: 'clear and strategic',
    defaultFocus: 'project health, org progress, top-level reporting',
    jargonLevel: 'simple',
    proactiveAgenda: false,
    taskReschedulingViaChat: false,
    greeting: 'Here\'s your executive overview.',
    quickActions: ['Project health summary', 'Milestone tracker', 'Team performance'],
  },
  admin: {
    tone: 'friendly and informative',
    defaultFocus: 'project health, org progress, upper-level reporting, urgent updates',
    jargonLevel: 'simple',
    proactiveAgenda: true,
    taskReschedulingViaChat: false,
    greeting: 'Let me catch you up on what needs attention.',
    quickActions: ['Urgent updates', 'Overdue tasks', 'Team workload', 'Resource conflicts'],
  },
  manager: {
    tone: 'clear and specific',
    defaultFocus: 'current task/project health, team-specific updates',
    jargonLevel: 'simple',
    proactiveAgenda: true,
    taskReschedulingViaChat: false,
    greeting: 'Your team\'s projects are loaded. Let\'s check in.',
    quickActions: ['My team status', 'Blocked tasks', 'This week\'s priorities'],
  },
  user: {
    tone: 'encouraging and guiding',
    defaultFocus: 'current task priorities, practical help, feature guidance',
    jargonLevel: 'simple',
    proactiveAgenda: true,
    taskReschedulingViaChat: true,
    greeting: 'Ready to help you get things done! 💪',
    quickActions: ['What should I work on next?', 'Show my tasks', 'Help me plan my day'],
  },
  visitor: {
    tone: 'friendly and guiding',
    defaultFocus: 'guided step-by-step task view/approval',
    jargonLevel: 'simple',
    proactiveAgenda: false,
    taskReschedulingViaChat: false,
    greeting: 'Welcome! I\'m here to guide you through everything.',
    quickActions: ['Show pending approvals', 'Help me navigate', 'What can I do here?'],
  },
};

/** What each role can access */
export interface RolePermissions {
  canManageInstances: boolean;
  canManageUsers: boolean;
  canManageTeams: boolean;
  canManageSubscription: boolean;
  canCreateProjects: boolean;
  canCreateTasks: boolean;
  canEditAnyTask: boolean;
  canEditOwnTasks: boolean;
  canViewAllSpaces: boolean;
  canViewAssignedSpacesOnly: boolean;
  canUseBuilder: boolean;
  canViewGanttToolbar: boolean;
  canRescheduleViaDrag: boolean;
  canRescheduleViaChat: boolean;
  simpleAdvancedToggle: boolean;
  canEnterOtherInstances: boolean;
  canCreateAutomations: boolean;
  /** Automations limited to self-directed actions only */
  automationsSelfOnly: boolean;
}

export const ROLE_PERMISSIONS: Record<AppRole, RolePermissions> = {
  architect: {
    canManageInstances: true,
    canManageUsers: true,
    canManageTeams: true,
    canManageSubscription: true,
    canCreateProjects: true,
    canCreateTasks: true,
    canEditAnyTask: true,
    canEditOwnTasks: true,
    canViewAllSpaces: true,
    canViewAssignedSpacesOnly: false,
    canUseBuilder: true,
    canViewGanttToolbar: true,
    canRescheduleViaDrag: true,
    canRescheduleViaChat: true,
    simpleAdvancedToggle: false,
    canEnterOtherInstances: true,
    canCreateAutomations: true,
    automationsSelfOnly: false,
  },
  owner: {
    canManageInstances: false,
    canManageUsers: true,
    canManageTeams: true,
    canManageSubscription: true,
    canCreateProjects: true,
    canCreateTasks: true,
    canEditAnyTask: true,
    canEditOwnTasks: true,
    canViewAllSpaces: true,
    canViewAssignedSpacesOnly: false,
    canUseBuilder: true,
    canViewGanttToolbar: true,
    canRescheduleViaDrag: true,
    canRescheduleViaChat: true,
    simpleAdvancedToggle: false,
    canEnterOtherInstances: false,
    canCreateAutomations: true,
    automationsSelfOnly: false,
  },
  admin: {
    canManageInstances: false,
    canManageUsers: true,
    canManageTeams: true,
    canManageSubscription: false,
    canCreateProjects: true,
    canCreateTasks: true,
    canEditAnyTask: true,
    canEditOwnTasks: true,
    canViewAllSpaces: true,
    canViewAssignedSpacesOnly: false,
    canUseBuilder: true,
    canViewGanttToolbar: true,
    canRescheduleViaDrag: true,
    canRescheduleViaChat: true,
    simpleAdvancedToggle: false,
    canEnterOtherInstances: false,
    canCreateAutomations: true,
    automationsSelfOnly: false,
  },
  manager: {
    canManageInstances: false,
    canManageUsers: true,
    canManageTeams: true,
    canManageSubscription: false,
    canCreateProjects: true,
    canCreateTasks: true,
    canEditAnyTask: true,
    canEditOwnTasks: true,
    canViewAllSpaces: false,
    canViewAssignedSpacesOnly: true,
    canUseBuilder: true,
    canViewGanttToolbar: true,
    canRescheduleViaDrag: true,
    canRescheduleViaChat: true,
    simpleAdvancedToggle: false,
    canEnterOtherInstances: false,
    canCreateAutomations: true,
    automationsSelfOnly: false,
  },
  user: {
    canManageInstances: false,
    canManageUsers: false,
    canManageTeams: false,
    canManageSubscription: false,
    canCreateProjects: false,
    canCreateTasks: false, // only in personal space
    canEditAnyTask: false,
    canEditOwnTasks: true,
    canViewAllSpaces: false,
    canViewAssignedSpacesOnly: true,
    canUseBuilder: false,
    canViewGanttToolbar: true,
    canRescheduleViaDrag: false,
    canRescheduleViaChat: true,
    simpleAdvancedToggle: true,
    canEnterOtherInstances: false,
    canCreateAutomations: true,
    automationsSelfOnly: true,
  },
  visitor: {
    canManageInstances: false,
    canManageUsers: false,
    canManageTeams: false,
    canManageSubscription: false,
    canCreateProjects: false,
    canCreateTasks: false,
    canEditAnyTask: false,
    canEditOwnTasks: false,
    canViewAllSpaces: false,
    canViewAssignedSpacesOnly: true,
    canUseBuilder: false,
    canViewGanttToolbar: false,
    canRescheduleViaDrag: false,
    canRescheduleViaChat: false,
    simpleAdvancedToggle: false,
    canEnterOtherInstances: false,
    canCreateAutomations: false,
    automationsSelfOnly: false,
  },
};

/** Check if roleA is at least as high as roleB in the hierarchy */
export function isRoleAtLeast(role: AppRole, minRole: AppRole): boolean {
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[minRole];
}

/** Get permissions for a role */
export function getPermissions(role: AppRole): RolePermissions {
  return ROLE_PERMISSIONS[role];
}

/** Roles that should be hidden from public user lists */
export const HIDDEN_ROLES: AppRole[] = ['architect'];

/** Roles visible in user management UIs */
export const MANAGEABLE_ROLES: AppRole[] = ['owner', 'admin', 'manager', 'user', 'visitor'];
